import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getWellnessCheck, updateLastWatchedAt } from "../../api/wellnessCheck";
import { wellnessChecksKeys } from "./queryKey";
import { useEffect } from "react";
import { WellnessCheck } from "../../api/generated";
import { useSyncWellnessCheckCache } from "./useSyncWellnessCheckCache";
import { useAuth } from "../auth/hooks/useAuth";

export const useWellnessCheck = (id: string) => {
    const queryClient = useQueryClient();
    const { csrfToken } = useAuth();

    const {
        data: wellnessCheck,
        isLoading,
        isSuccess,
    } = useQuery({
        queryKey: wellnessChecksKeys.detail(id),
        queryFn: () => getWellnessCheck(id),
        initialData: () => {
            const allWellnessChecks = queryClient.getQueryData<WellnessCheck[]>(
                wellnessChecksKeys.all
            );
            return allWellnessChecks?.find(
                (wellnessCheck) => wellnessCheck.id === id
            );
        },
    });

    const { syncWellnessCheckCache } = useSyncWellnessCheckCache();

    const { mutate: updateLastWatchedAtMutate } = useMutation({
        mutationFn: (param: { id: string; csrfToken: string }) =>
            updateLastWatchedAt(param.id, param.csrfToken),
        onSuccess(data: WellnessCheck) {
            syncWellnessCheckCache(data);
        },
    });

    useEffect(() => {
        if (isSuccess) {
            updateLastWatchedAtMutate({ id, csrfToken });
        }
    }, [isSuccess, updateLastWatchedAtMutate, id, csrfToken]);

    return {
        wellnessCheck: wellnessCheck,
        isLoading: isLoading,
    };
};
